import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useLocale } from '../contexts/LocaleContext'
import { Mail, Lock, LogIn, UserPlus, AlertCircle, CheckCircle, Star, TrendingUp, ArrowLeft, Shield } from 'lucide-react'
import { isSupabaseConfigured } from '../lib/supabase'

interface LoginPageProps {
  onSuccess: () => void
}

// Mock proposal card component for background
function ProposalMockup({ className, variant }: { className?: string; variant: 'proposal' | 'stats' | 'signature' }) {
  if (variant === 'proposal') {
    return (
      <div className={`bg-white/90 rounded-2xl shadow-2xl p-6 w-72 ${className}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-accent-500 rounded-lg" />
          <div>
            <div className="h-3 w-24 bg-slate-200 rounded" />
            <div className="h-2 w-16 bg-slate-100 rounded mt-1" />
          </div>
        </div>
        <div className="h-2.5 w-full bg-slate-100 rounded mb-2" />
        <div className="h-2.5 w-4/5 bg-slate-100 rounded mb-4" />
        <div className="border-t border-slate-100 pt-4 mt-4">
          <div className="flex justify-between items-center mb-3">
            <div className="h-2 w-16 bg-slate-100 rounded" />
            <div className="h-4 w-20 bg-emerald-100 rounded-full" />
          </div>
          <div className="flex justify-between items-center">
            <div className="h-2 w-20 bg-slate-100 rounded" />
            <div className="h-5 w-24 bg-slate-500/20 rounded font-bold text-slate-700 flex items-center justify-center text-xs">$12,500</div>
          </div>
        </div>
      </div>
    )
  }

  if (variant === 'stats') {
    return (
      <div className={`bg-white/90 rounded-2xl shadow-2xl p-5 w-64 ${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-emerald-500" />
          <span className="text-sm font-medium text-slate-700">Statistics</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-slate-700">85%</div>
            <div className="text-[10px] text-slate-500">Proposals signed</div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-emerald-600">$45K</div>
            <div className="text-[10px] text-slate-500">Monthly revenue</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`bg-white/90 rounded-2xl shadow-2xl p-5 w-56 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle className="w-5 h-5 text-emerald-500" />
        <span className="text-sm font-medium text-slate-700">Signed successfully</span>
      </div>
      <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
        <div className="h-8 border-b-2 border-slate-400 mb-2" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 30'%3E%3Cpath d='M10 25 Q 30 5, 50 20 T 90 15' stroke='%23db2777' fill='none' stroke-width='2'/%3E%3C/svg%3E")`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center'
        }} />
        <div className="text-[10px] text-slate-400 text-center">John Smith • 01/15/2025</div>
      </div>
    </div>
  )
}

export default function LoginPage({ onSuccess }: LoginPageProps) {
  const { signIn, signUp, signInWithGoogle } = useAuth()
  const { tx } = useLocale()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [isSignUp, setIsSignUp] = useState(searchParams.get('signup') === '1')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (isSignUp) {
        await signUp(email, password, '', '')
        if (isSupabaseConfigured()) {
          setError(tx('A verification email has been sent to your inbox. Please check your email.', 'נשלח אליך אימייל אימות. אנא בדוק את תיבת הדואר שלך.'))
          setIsSignUp(false)
        } else {
          onSuccess()
        }
      } else {
        await signIn(email, password)
        onSuccess()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : tx('Sign in failed', 'ההתחברות נכשלה'))
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setError(null)
    setLoading(true)
    try {
      await signInWithGoogle()
    } catch (err) {
      setError(err instanceof Error ? err.message : tx('Google sign in failed', 'ההתחברות עם Google נכשלה'))
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden" dir="ltr">
      {/* Dark gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900" />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />

      {/* Gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-slate-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-rose-500/20 rounded-full blur-3xl" />
      <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />

      {/* Blurred proposal mockups scattered in background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Top left */}
        <div className="absolute -top-10 -left-10 transform rotate-12 blur-[2px] opacity-60 scale-90">
          <ProposalMockup variant="proposal" />
        </div>

        {/* Top right */}
        <div className="absolute top-20 -right-16 transform -rotate-12 blur-[2px] opacity-50 scale-75">
          <ProposalMockup variant="stats" />
        </div>

        {/* Middle left */}
        <div className="absolute top-1/3 -left-20 transform rotate-6 blur-[3px] opacity-40 scale-80">
          <ProposalMockup variant="signature" />
        </div>

        {/* Middle right */}
        <div className="absolute top-1/2 -right-24 transform -rotate-6 blur-[2px] opacity-50 scale-85">
          <ProposalMockup variant="proposal" />
        </div>

        {/* Bottom left */}
        <div className="absolute bottom-20 -left-16 transform -rotate-12 blur-[2px] opacity-50 scale-75">
          <ProposalMockup variant="stats" />
        </div>

        {/* Bottom right */}
        <div className="absolute bottom-10 -right-10 transform rotate-12 blur-[3px] opacity-40 scale-90">
          <ProposalMockup variant="signature" />
        </div>

        {/* Additional floating elements */}
        <div className="absolute top-40 left-1/4 transform rotate-3 blur-[2px] opacity-30 scale-70">
          <ProposalMockup variant="proposal" />
        </div>

        <div className="absolute bottom-40 right-1/4 transform -rotate-6 blur-[2px] opacity-35 scale-80">
          <ProposalMockup variant="stats" />
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Back to home */}
          <div className="text-center mb-4">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-1 text-sm text-slate-300/60 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {tx('Back to Home', 'חזרה לדף הבית')}
            </button>
          </div>

          {/* Logo */}
          <div className="text-center mb-8">
            <img src="/media/quor.png" alt="QUOR - Digital Signatures Made Simple" className="h-16 mx-auto mb-4" />
            <p className="text-slate-200/80 mt-2">{tx('Business proposals & digital signatures', 'הצעות עסקיות וחתימות דיגיטליות')}</p>
          </div>

          {/* Glassmorphism Form Card */}
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl p-8">
            {/* Headline */}
            <h2 className="text-2xl font-bold text-white mb-1 text-center">
              {isSignUp
                ? tx('Start closing deals in 2 minutes', 'התחל לסגור עסקאות תוך 2 דקות')
                : tx('Welcome back', 'ברוך שובך')}
            </h2>

            {/* Subheadline — signup only */}
            {isSignUp && (
              <p className="text-center text-slate-300/80 text-sm mb-1">
                {tx('No credit card required. 3 proposals free forever.', 'ללא כרטיס אשראי. 3 הצעות בחינם לתמיד.')}
              </p>
            )}

            {/* Social proof — signup only */}
            {isSignUp && (
              <p className="text-center text-slate-400/70 text-xs mb-5">
                {tx('Join 1,200+ freelancers and agencies', 'הצטרף ל-1,200+ פרילנסרים וסוכנויות')}
              </p>
            )}

            {!isSignUp && <div className="mb-5" />}

            {error && (
              <div className={`flex items-center gap-2 p-4 rounded-xl mb-6 backdrop-blur-sm ${
                error.includes('verification')
                  ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/30'
                  : 'bg-red-500/20 text-red-200 border border-red-500/30'
              }`}>
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Demo Mode Notice */}
            {!isSupabaseConfigured() && (
              <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl p-4 mb-6 backdrop-blur-sm">
                <p className="text-amber-200 text-sm text-center">
                  <strong>{tx('Demo Mode', 'מצב הדגמה')}</strong> - {tx('Enter any email address to sign in', 'הזינו כתובת אימייל כלשהי כדי להתחבר')}
                </p>
              </div>
            )}

            {/* Google Sign In — PRIMARY option, shown first */}
            {isSupabaseConfigured() && (
              <>
                <button
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-white text-slate-800 py-3.5 px-4 rounded-xl font-semibold hover:bg-slate-100 focus:ring-4 focus:ring-white/20 transition-all disabled:opacity-50 shadow-md mb-4"
                >
                  <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  <span>{tx('Continue with Google', 'המשך עם Google')}</span>
                </button>

                <div className="relative mb-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/20"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-transparent text-slate-300/70">{tx('or', 'או')}</span>
                  </div>
                </div>
              </>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">{tx('Email Address', 'כתובת אימייל')}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-300/50" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-300/50 focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-all backdrop-blur-sm"
                    placeholder={tx('your@email.com', 'האימייל@שלך.com')}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-slate-200">{tx('Password', 'סיסמה')}</label>
                  {!isSignUp && (
                    <a
                      href="/forgot-password"
                      className="text-xs text-slate-300/70 hover:text-white transition-colors"
                    >
                      {tx('Forgot password?', 'שכחת סיסמה?')}
                    </a>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-300/50" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required={isSupabaseConfigured()}
                    minLength={6}
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-300/50 focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-all backdrop-blur-sm"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              {/* Privacy Consent - Required for signup (Privacy Law Section 13) */}
              {isSignUp && (
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-white/30 bg-white/10 text-slate-500 focus:ring-slate-400 focus:ring-offset-0"
                  />
                  <label htmlFor="acceptTerms" className="text-sm text-slate-200/80 leading-tight">
                    {tx('I have read and agree to the', 'קראתי ואני מסכים/ה ל')}
                    <a href="/terms" target="_blank" className="text-slate-300 hover:text-white underline mx-1">{tx('Terms of Service', 'תנאי השימוש')}</a>
                    {tx('and', 'ול')}
                    <a href="/privacy" target="_blank" className="text-slate-300 hover:text-white underline mx-1">{tx('Privacy Policy', 'מדיניות הפרטיות')}</a>
                    {tx('including collection and processing of personal data', 'כולל איסוף ועיבוד מידע אישי')}
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || (isSignUp && !acceptedTerms)}
                className="w-full flex items-center justify-center gap-2 bg-accent-500 text-white py-3 px-4 rounded-xl font-semibold hover:bg-accent-600 focus:ring-4 focus:ring-accent-500/30 transition-all disabled:opacity-50 shadow-lg shadow-accent-500/30"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : isSignUp ? (
                  <>
                    <UserPlus className="w-5 h-5" />
                    <span>{tx('Create Free Account', 'צור חשבון חינם')}</span>
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>{tx('Sign In', 'התחברות')}</span>
                  </>
                )}
              </button>
            </form>

            {/* Trust badge */}
            <div className="flex items-center justify-center gap-1.5 mt-5 text-slate-400/60 text-xs">
              <Shield className="w-3.5 h-3.5" />
              <span>{tx('Secure · No spam · Cancel anytime', 'מאובטח · ללא ספאם · ביטול בכל עת')}</span>
            </div>

            {/* Toggle */}
            <p className="text-center mt-4 text-slate-200/70 text-sm">
              {isSignUp
                ? tx('Already have an account?', 'כבר יש לך חשבון?')
                : tx("Don't have an account yet?", 'אין לך חשבון עדיין?')}
              <button
                onClick={() => {
                  setIsSignUp(!isSignUp)
                  setError(null)
                  setAcceptedTerms(false)
                }}
                className="text-slate-300 hover:text-white font-medium ml-2 transition-colors"
              >
                {isSignUp ? tx('Sign In', 'התחברות') : tx('Sign Up for Free', 'הרשמה חינם')}
              </button>
            </p>
          </div>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-slate-300/50 text-sm">
              {tx('A platform for business proposals and digital signatures', 'פלטפורמה להצעות עסקיות וחתימות דיגיטליות')}
            </p>
            <div className="flex items-center justify-center gap-1 mt-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
              ))}
              <span className="text-slate-300/50 text-xs ml-2">{tx('4.9/5 from 1,200+ clients', '4.9/5 מתוך 1,200+ לקוחות')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
